import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_ROWS_PER_PAGE,
} from "../constants";

import {
  dateAtTimezone,
  DEFAULT_US_DATE_FORMAT,
  isDateValid,
} from "./dateUtil";

interface Column {
  id: string;
  [key: string]: unknown;
}

interface DataRecord {
  [key: string]: unknown;
}

export interface SelectionStrategy {
  strategy: number;
  mapping?: Map<number, boolean>;
  checked?: boolean;
}

/**
 * Escape values to support in a RegExp
 * @param  {string} value               the value
 * @return {string}       escaped value
 */
export const escapeRegExp = (value: string): string => {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
};

/**
 * Search on dataset using fields displayed in the UI only
 * @param {Array} columns     list of columns
 * @param {Array} dataset     dataset to filter
 * @param {string} searchText  text to match
 * @return {Array}            a filtered version of the dataset
 */
export const searchDataset = (
  columns: Column[],
  dataset: DataRecord[],
  searchText: string,
): DataRecord[] => {
  const fieldsOnUi = new Set(columns.map((item) => item.id));
  const searchRegex = new RegExp(escapeRegExp(searchText), "i");

  const filtered = dataset.filter((row) =>
    Object.keys(row).some(
      (field) =>
        fieldsOnUi.has(field) &&
        row[field] &&
        searchRegex.test(String(row[field])),
    ),
  );

  return filtered;
};

/**
 * When presenting a select all checkbox in a column, what strategy should be
 * use to select rows
 * @type {Enum}
 */
export const RowSelectionStrategy = {
  DISABLED: 0,
  ALL_ROWS: 1,
  BY_PAGE: 2,
} as const;

/* interface definition */
export const selectionStrategyDefault: SelectionStrategy = {
  strategy: RowSelectionStrategy.DISABLED,
  mapping: undefined,
  checked: undefined,
};

/**
 * Whether the selectionStrategy is diff than DISABLED
 * @param  {Object}  selectionStrategy               selection strategy
 * @return {Boolean}                   true when strategy is diff than DISABLED
 */
export const isSelectionStrategyEnabled = (
  selectionStrategy: SelectionStrategy | null | undefined,
): boolean => {
  const resolving = selectionStrategy ?? selectionStrategyDefault;

  return resolving.strategy !== RowSelectionStrategy.DISABLED;
};

/**
 * Returns the checked value based on selectionStrategy
 * @param  {Object} selectionStrategy               selection strategy
 * @param  {number} currentPage                     page number
 * @return {boolean}                   is checked value
 */
export const selectionStrategyResolver = (
  selectionStrategy: SelectionStrategy | null | undefined,
  currentPage: number,
): boolean => {
  const resolving = selectionStrategy ?? selectionStrategyDefault;

  if (resolving.strategy === RowSelectionStrategy.ALL_ROWS) {
    return resolving.checked ?? false;
  } else if (resolving.strategy === RowSelectionStrategy.BY_PAGE) {
    return resolving.mapping?.get(currentPage) ?? false;
  }

  return false;
};

/**
 * Calculate the list of page sizes to support by a MuiDataGrid
 * @param  {number} defaultRowsPerPage               default rows per page value
 * @return {Array}                    list of page sizes
 */
export const calcPageSizeOptions = (defaultRowsPerPage?: number): number[] => {
  if (defaultRowsPerPage) {
    return [defaultRowsPerPage].concat(DATAGRID_ROWS_PER_PAGE);
  } else {
    return [DATAGRID_DEFAULT_PAGE_SIZE].concat(DATAGRID_ROWS_PER_PAGE);
  }
};

/**
 * Create a rowId to each record iif needed
 * @param  {Array}              dataset
 * @return {Array}              altered dataset
 */
export const mapRowIdentifier = (dataset: DataRecord[] = []): DataRecord[] => {
  return typeof dataset[0] === "object" && dataset[0]["id"] == null
    ? dataset.map((record, idx) => {
        return {
          ...record,
          __rowId: idx,
        };
      })
    : dataset;
};

export interface LastUpdatedRow {
  lastUpdatedBy?: string | null;
  lastUpdated?: string | null;
  [key: string]: unknown;
}

export const formatLastUpdated = (
  row: LastUpdatedRow,
  timezone: string,
  format: string = DEFAULT_US_DATE_FORMAT,
): string => {
  let userName,
    dateTime = null;
  const lastUpdatedBy = row.lastUpdatedBy || "system";
  const lastUpdated = row.lastUpdated;

  userName = lastUpdatedBy;
  if (userName && userName.includes("@")) {
    userName = userName.split("@")[0];
  }

  dateTime =
    lastUpdated && isDateValid(lastUpdated)
      ? dateAtTimezone(lastUpdated, timezone, format)
      : "No date found";

  return `${dateTime} ${userName}`;
};

export const formatDateAtTimezoneOrDefault = (
  value: string | null | undefined,
  timezone: string,
  defaultValue: string,
): string => {
  if (value && isDateValid(value)) return dateAtTimezone(value, timezone);

  return defaultValue;
};
